import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { useDynamicExperienceContext } from './DynamicExperienceProvider';
import useFlowTrackingEvents, {
  createHostElementEventContext,
  useTrackingEventTriggers,
} from '@de/hooks/useFlowTrackingEvents';
import {
  buildStepCorrelation,
  type StepCorrelation,
} from '@de/events/stepCorrelation';
import type { DynamicExperienceEventContext } from '@de/events/buildEventContext';
import { useEntryPointEventFiring } from './useEntryPointEventFiring';
import { contentDeclaresProductSurface } from '@de/utils/configurable-step/contentDeclaresProductSurface';
import { buildEntryFieldSummary } from '@de/utils/configurable-step/buildEntryFieldSummary';
import { extractTrackingConfig } from '@de/utils/configurable-step/extractTrackingConfig';
import type {
  CustomDimensionMapping,
  ProductFieldMapping,
} from '@de/utils/configurable-step';
import {
  buildConfiguredEventContext,
  readRootExperienceTracking,
  type ConfiguredEventContext,
} from '@de/utils/configured-events/index';
import {
  useForwardStepEventFiring,
  type ForwardStepClickContext,
} from './useForwardStepEventFiring';
import {
  type HostFormContext,
  type TrackingHostContext,
} from './TrackingHostContext';
import type { TrackingMeta } from '@de/components/Trackable/Trackable';
import { createHostTrackingPage } from '@de/utils/configurable-step/resolveElementFields';
import tagularEvent from '@de/utils/tagularEvent';
import type { HostEventTransportResult } from '@de/utils/hostEventTransport';
import {
  appendBindingDedupKey,
  composeFlowElementKey,
  composeHostElementKey,
  type BindingDedupIdentity,
} from '@de/utils/composeHostElementKey';

export type { ForwardStepClickContext } from './useForwardStepEventFiring';
export type {
  HostFormContext,
  TrackingHostContext,
  TrackingHostPolicy,
} from './TrackingHostContext';

/**
 * Trigger surface a DSL component (via `Trackable`) calls to emit
 * in-flow events. Sourced from `useFlowTrackingEvents` — every entry
 * here is the existing trigger function from the standard render
 * path. Exposing the same functions means the DSL render path
 * satisfies the abstraction's "both render paths without divergence"
 * requirement.
 */
export interface TrackingTriggers {
  readonly triggerElementLoadedEvent: ReturnType<
    typeof useFlowTrackingEvents
  >['triggerElementLoadedEvent'];
  readonly triggerProductLoadedEvent: ReturnType<
    typeof useFlowTrackingEvents
  >['triggerProductLoadedEvent'];
  readonly triggerStepElementLoadedEvent: ReturnType<
    typeof useFlowTrackingEvents
  >['triggerStepElementLoadedEvent'];
  readonly triggerStepElementViewedEvent: ReturnType<
    typeof useFlowTrackingEvents
  >['triggerStepElementViewedEvent'];
  readonly triggerElementViewedEvent: ReturnType<
    typeof useFlowTrackingEvents
  >['triggerElementViewedEvent'];
  readonly triggerElementClickedEvent: ReturnType<
    typeof useFlowTrackingEvents
  >['triggerElementClickedEvent'];
  readonly triggerProductClickedEvent: ReturnType<
    typeof useFlowTrackingEvents
  >['triggerProductClickedEvent'];
  /**
   * `kind: 'product'` on a tracked DSL component fires `ProductViewed`
   * on intersect. Merges a partial override (StepCorrelation
   * customDimensions + per-component location / ctaText etc.) on top
   * of the inherited `eventContext`.
   */
  readonly triggerProductViewedEvent: ReturnType<
    typeof useFlowTrackingEvents
  >['triggerProductViewedEvent'];
  readonly triggerFieldEvents: ReturnType<
    typeof useFlowTrackingEvents
  >['triggerFieldEvents'];
  readonly triggerFormStartedEvent: ReturnType<
    typeof useFlowTrackingEvents
  >['triggerFormStartedEvent'];
  readonly triggerFormContinuedEvent: ReturnType<
    typeof useFlowTrackingEvents
  >['triggerFormContinuedEvent'];
  /**
   * Entry-step intersect firer (§6.1: `FormViewed` generates
   * `formViewedCorrelationId` for the form context the entry step
   * introduces). NOT used for non-entry steps —
   * `useEntryPointEventFiring` fires `triggerStepElementViewedEvent`
   * on non-entry intersect per §7.3.
   *
   * Mirrors `triggerProductLoadedEvent` (partial-context override
   * merged on top of inherited eventContext).
   */
  readonly triggerFormViewedEvent: ReturnType<
    typeof useFlowTrackingEvents
  >['triggerFormViewedEvent'];
  readonly triggerFormEvents: ReturnType<
    typeof useFlowTrackingEvents
  >['triggerFormEvents'];
}

/**
 * Entry-point correlation IDs surfaced for downstream consumers
 * (submit/click events inherit this block).
 *
 * Inherited from `eventContext` — `TrackingProvider` does NOT
 * generate its own correlation IDs (Section §11.1 of the spec
 * defines the canonical IDs on the standard render path; the DSL
 * path participates in the same chain).
 */
export interface EntryPointCorrelation {
  readonly elementLoadedCorrelationId: string | undefined;
  readonly viewCorrelationId: string | undefined;
  readonly formViewedCorrelationId: string | undefined;
}

export interface TrackingContextValue {
  /**
   * Host identity: the metadata-only resolve root, the fire-once
   * dedup scope, and the dispatch policy the mounting host declared.
   * `flow-defaults` hosts activate tracked nodes by default;
   * `explicit-node-enable` hosts emit only for nodes that opt in.
   */
  readonly host: TrackingHostContext;

  /**
   * Inherited entry-point event context (correlation IDs, formName,
   * customDimensions, action outcome) — owned by the standard
   * `DynamicExperienceProvider` so the DSL render path participates
   * in the same correlation chain.
   *
   * `undefined` when the provider is mounted outside a
   * `DynamicExperienceProvider`. Consumers should treat that as a
   * "no flow context" signal and either skip firing or log.
   */
  readonly eventContext: DynamicExperienceEventContext | undefined;

  /**
   * Per-step correlation rebuilt on every step transition. Stable
   * reference within a single step render.
   *
   * Step-free on `explicit-node-enable` hosts: they have no step, so
   * every field is empty and nothing step-shaped can reach the wire.
   */
  readonly stepCorrelation: StepCorrelation;

  /** Trigger surface — see {@link TrackingTriggers}. */
  readonly triggers: TrackingTriggers;

  /**
   * Entry-point correlation IDs inherited from `eventContext`,
   * exposed as a named block so submit/click consumers don't need to
   * reach into the full event context.
   *
   * Every field is `undefined` when the provider is mounted outside
   * a `DynamicExperienceProvider` (mirrors the
   * `eventContext === undefined` no-op contract).
   */
  readonly entryPointCorrelation: EntryPointCorrelation;

  /**
   * Per-provider-session identifier inherited from
   * `DynamicExperienceProvider`. Consumers that maintain their own
   * fire-once gates thread this through {@link composeDedupKey} so a
   * fresh provider mount does not silently inherit dedup state from
   * a prior session.
   *
   * `undefined` when the provider is mounted outside a
   * `DynamicExperienceProvider`.
   */
  readonly flowSessionId: string | undefined;

  /**
   * Host-scoped fire-once key for a rendered node. Owned by the
   * provider because the dedup scope is a property of the host, not
   * of the node: flow hosts key on flow session + config + step,
   * registered-default hosts key on host mount + config. Consumers pass the
   * node identity (Puck node id, plus any repeater index path) and
   * stay out of the scoping rules. A second argument appends a
   * configured-binding identity so that key cannot equal the
   * out-of-the-box key.
   */
  readonly composeDedupKey: (
    nodeId: string,
    binding?: BindingDedupIdentity,
  ) => string;

  /**
   * Ref callback that the host (`ConfigurableStep`'s entry-root
   * wrapper) wires to the DSL subtree's DOM root. The
   * `useEntryPointEventFiring` hook observes the attached node and
   * fires `ProductViewed` + `FormViewed` once it crosses the
   * intersection threshold.
   *
   * The callback identity is stable across renders (memoized with
   * empty deps) so re-rendering `ConfigurableStep` does NOT tear
   * down and re-mount the underlying `IntersectionObserver`. No-op
   * on `explicit-node-enable` hosts, which have no entry point.
   */
  readonly setEntryRootNode: (node: HTMLElement | null) => void;

  /**
   * Spec-compliant forward-click event firer. Owned by
   * `useForwardStepEventFiring` and exposed here for direct
   * consumers (unit tests, and any future DSL caller that wants to
   * drive the forward-click sequence without going through
   * `useHandleStepForward`).
   *
   * Under the hood, the same firer is also written into a ref cell
   * supplied by `DynamicExperienceProvider` so `useHandleStepForward`
   * (which sits ABOVE `TrackingProvider` in the tree and therefore
   * cannot consume `useTracking()`) can delegate forward-click event
   * firing to the provider — keeping `FormStarted` / `ProductClicked`
   * / `FormContinued` orchestration in a single place per Confluence
   * "Dynamic Experience Eventing Requirements" §6.1 / §6.7.
   *
   * No-op when the provider is mounted outside a
   * `DynamicExperienceProvider`, and on `explicit-node-enable` hosts
   * (no step flow to advance).
   */
  readonly fireForwardStepClick: (ctx: ForwardStepClickContext) => void;
  /**
   * Set synchronously by `createBoundRender`'s click dispatch when a
   * configured click binding suppressed or replaced any component's
   * built-in `ProductClicked` on this click. `useForwardStepEventFiring`
   * reads and clears it before firing its own, independent entry
   * `ProductClicked` so the two mechanisms do not both send one for the
   * same click. Undefined on hosts that never fire forward-step events
   * (`DefaultOnTrackingProvider`).
   */
  readonly entryProductClickSuppressedRef?: React.MutableRefObject<boolean>;
  /**
   * Dual-shape `tracking.productFields` from the current step's DSL
   * root. EL/Trackable overlays this onto repeater identity. Missing
   * when the current step has no tracking block.
   */
  readonly productFields?: Readonly<Record<string, ProductFieldMapping>>;
  /**
   * Sibling `tracking.customDimensions` mappings from the current
   * step's DSL root. Resolved at emit against the repeater `item`.
   */
  readonly customDimensionMappings?: readonly CustomDimensionMapping[];
  /**
   * Step-level values a configured click can read through
   * `experience.<key>`. `pageCategory` and `contentGroup` come from
   * the DSL root tracking block. Built-in payloads keep reading
   * `stepCorrelation`, which does not carry the config version.
   */
  readonly configuredEventContext?: ConfiguredEventContext;
  /**
   * Send one already composed configured event. Click bindings call
   * this once per interaction. It does not use the mount/view dedupe
   * keys.
   */
  readonly sendConfiguredEvent: (
    eventName: string,
    payload: Record<string, unknown>,
  ) => HostEventTransportResult;
}

const TrackingContext = createContext<TrackingContextValue | undefined>(
  undefined,
);

export interface TrackingProviderProps {
  /**
   * DSL config id resolved by the host bridge (`ConfigurableStep`).
   * Surfaced on `StepCorrelation.dslConfigId` so downstream funnel
   * analysis can attribute in-flow events back to the published DSL
   * configuration that produced the step.
   */
  readonly dslConfigId?: string;
  /**
   * Optional explicit page category (overrides the default
   * `dexp-step` from `buildStepCorrelation`). Use for non-step
   * surfaces (results pages, modals) that mount their own
   * `TrackingProvider` outside the standard step flow.
   */
  readonly pageCategory?: string;
  /**
   * Published DSL config version. Included on `stepCorrelation` and
   * `configuredEventContext` when it is an integer of at least 1.
   * Absent, `0`, and non-integers are omitted.
   */
  readonly dslConfigVersion?: number;
  readonly children: ReactNode;
}

/**
 * Mounts the per-step tracking context for the DSL render path.
 *
 * Reads the canonical `eventContext`, `currentStep`, and
 * `currentStepIndex` from the surrounding `DynamicExperienceProvider`
 * so DSL steps inherit the same correlation IDs the standard path
 * uses. Builds a fresh `StepCorrelation` per render of the current
 * step, and pulls every trigger function from
 * `useFlowTrackingEvents` so DSL nodes never need to call
 * `tagularEvent` directly.
 *
 * Per Eventing Architecture SAD §6.6 the long-term direction is for
 * `Trackable` to delegate event capture to a future
 * `@highereducation/eventing` package. Until that package lands the
 * provider mounts the existing trigger functions inline; the public
 * surface (`useTracking()` returning `eventContext`,
 * `stepCorrelation`, and `triggers`) is shaped to absorb that swap
 * without breaking DSL component code.
 */
export const TrackingProvider: React.FC<TrackingProviderProps> = ({
  dslConfigId,
  dslConfigVersion,
  pageCategory,
  children,
}) => {
  const ctx = useDynamicExperienceContext() as Partial<
    ReturnType<typeof useDynamicExperienceContext>
  >;
  const triggers = useFlowTrackingEvents();

  const eventContext = ctx?.eventContext as
    | DynamicExperienceEventContext
    | undefined;
  const flowSessionId = ctx?.flowSessionId;
  const host = useMemo<TrackingHostContext & { policy: 'flow-defaults' }>(
    () => ({
      page: (eventContext ?? {}) as Readonly<Record<string, unknown>>,
      dedupScope: {
        sessionId: flowSessionId ?? '',
        configId: dslConfigId ?? '',
      },
      policy: 'flow-defaults',
    }),
    [eventContext, flowSessionId, dslConfigId],
  );

  const stepCorrelation = useMemo(
    () =>
      buildStepCorrelation({
        currentStep: ctx?.currentStep,
        currentStepIndex: ctx?.currentStepIndex,
        dslConfigId,
        dslConfigVersion,
        pageCategory,
      }),
    [
      ctx?.currentStep,
      ctx?.currentStepIndex,
      dslConfigId,
      dslConfigVersion,
      pageCategory,
    ],
  );

  const configuredEventContext = useMemo(() => {
    const dslData = (
      ctx?.currentStep as { dslConfig?: { data?: unknown } } | undefined
    )?.dslConfig?.data;
    const rootTracking = readRootExperienceTracking(dslData);
    return buildConfiguredEventContext({
      dslConfigId,
      dslConfigVersion,
      flowId: ctx?.currentStep?.flowId,
      stepId: stepCorrelation.stepId,
      stepIndex: stepCorrelation.stepIndex,
      pageCategory: rootTracking.pageCategory,
      contentGroup: rootTracking.contentGroup,
    });
  }, [ctx?.currentStep, dslConfigId, dslConfigVersion, stepCorrelation]);

  // Reassemble the trigger map so a stable object identity flows into
  // the entry-point hook and the context value. `useFlowTrackingEvents`
  // returns a fresh literal each render, so we cannot use the parent
  // object as a dep; depend on each individual `useCallback`-stable
  // trigger function instead. Net result: `trackingTriggers` only
  // changes when an underlying trigger's deps actually shift
  // (`eventContext` rebuild on step transition), avoiding extra
  // re-runs of `useEntryPointEventFiring` / `useForwardStepEventFiring`
  // effects on unrelated re-renders.
  const {
    triggerElementLoadedEvent,
    triggerProductLoadedEvent,
    triggerStepElementLoadedEvent,
    triggerStepElementViewedEvent,
    triggerElementViewedEvent,
    triggerElementClickedEvent,
    triggerProductClickedEvent,
    triggerProductViewedEvent,
    triggerFieldEvents,
    triggerFormStartedEvent,
    triggerFormContinuedEvent,
    triggerFormViewedEvent,
    triggerFormEvents,
  } = triggers;
  const trackingTriggers = useMemo<TrackingTriggers>(
    () => ({
      triggerElementLoadedEvent,
      triggerProductLoadedEvent,
      triggerStepElementLoadedEvent,
      triggerStepElementViewedEvent,
      triggerElementViewedEvent,
      triggerElementClickedEvent,
      triggerProductClickedEvent,
      triggerProductViewedEvent,
      triggerFieldEvents,
      triggerFormStartedEvent,
      triggerFormContinuedEvent,
      triggerFormViewedEvent,
      triggerFormEvents,
    }),
    [
      triggerElementLoadedEvent,
      triggerProductLoadedEvent,
      triggerStepElementLoadedEvent,
      triggerStepElementViewedEvent,
      triggerElementViewedEvent,
      triggerElementClickedEvent,
      triggerProductClickedEvent,
      triggerProductViewedEvent,
      triggerFieldEvents,
      triggerFormStartedEvent,
      triggerFormContinuedEvent,
      triggerFormViewedEvent,
      triggerFormEvents,
    ],
  );

  // Does the step's content declare product surfaces (including disabled
  // Product placements)? If so, those nodes claim the Product surface and
  // the entry unit must not emit a fallback Product lifecycle. Resolved
  // statically so it's correct before async data populates the repeater.
  const dslContent = (
    ctx?.currentStep as
      | { dslConfig?: { data?: { content?: unknown } } }
      | undefined
  )?.dslConfig?.data?.content;
  const contentEmitsProducts = useMemo(
    () => contentDeclaresProductSurface(dslContent),
    [dslContent],
  );

  // Field-composition summary (`fieldCount`/`fieldNames`/`fieldSet`) merged onto
  // the entry unit's Product events so a generic DSL-entry Product describes what
  // it contains. Empty for non-field entries.
  const entryFieldDimensions = useMemo(
    () => buildEntryFieldSummary(dslContent),
    [dslContent],
  );

  const trackingConfig = useMemo(() => {
    const dslConfig = (
      ctx?.currentStep as { dslConfig?: { data?: unknown } } | undefined
    )?.dslConfig;
    if (!dslConfig) return undefined;
    return extractTrackingConfig(dslConfig.data);
  }, [ctx?.currentStep]);
  const productFields = trackingConfig?.productFields;
  const customDimensionMappings = trackingConfig?.customDimensions;

  // Entry-point lifecycle firing for the DSL render path. The hook
  // is a no-op when `eventContext` is undefined (provider mounted
  // outside `DynamicExperienceProvider`) or when `currentStep` is
  // missing; both gates are evaluated inside the hook so the public
  // context surface stays additive.
  const { setEntryRootNode } = useEntryPointEventFiring({
    eventContext,
    currentStep: ctx?.currentStep,
    triggers: trackingTriggers,
    dslConfigId,
    dslConfigVersion,
    flowSessionId,
    contentEmitsProducts,
    entryFieldDimensions,
    productFields,
    customDimensionMappings,
    flowState: ctx?.flowState,
    steps: ctx?.steps,
  });

  // Forward-click event firing (FormStarted / ProductClicked /
  // FormContinued) for the user-driven Next-button advance. The
  // ref bridge is owned by `DynamicExperienceProvider` so
  // `useHandleStepForward` (mounted above this provider in the
  // tree) can invoke the firer from its click callback. The hook
  // also surfaces the firer on `TrackingContextValue` for direct
  // consumers / tests.
  // Bridged into `createBoundRender`'s click dispatch (via
  // `TrackingContextValue.entryProductClickSuppressedRef`) and read back
  // here so a configured click binding that suppressed or replaced any
  // component's built-in `ProductClicked` is not double-sent by this firer.
  const ownedEntryProductClickSuppressedRef = useRef(false);
  const entryProductClickSuppressedRef =
    ctx?.entryProductClickSuppressedRef ?? ownedEntryProductClickSuppressedRef;

  const { fireForwardStepClick } = useForwardStepEventFiring({
    eventContext,
    currentStep: ctx?.currentStep,
    currentStepIndex: ctx?.currentStepIndex,
    triggers: trackingTriggers,
    forwardStepEventFirerRef: ctx?.forwardStepEventFirerRef,
    formStartedFiredRef: ctx?.formStartedFiredRef,
    suppressedProductClickRef: entryProductClickSuppressedRef,
  });

  const entryPointCorrelation = useMemo<EntryPointCorrelation>(
    () => ({
      elementLoadedCorrelationId: eventContext?.elementLoadedCorrelationId,
      viewCorrelationId: eventContext?.viewCorrelationId,
      formViewedCorrelationId: eventContext?.formViewedCorrelationId,
    }),
    [
      eventContext?.elementLoadedCorrelationId,
      eventContext?.viewCorrelationId,
      eventContext?.formViewedCorrelationId,
    ],
  );

  return (
    <TrackingCoreProvider
      host={host}
      eventContext={eventContext}
      stepCorrelation={stepCorrelation}
      triggers={trackingTriggers}
      entryPointCorrelation={entryPointCorrelation}
      flowSessionId={flowSessionId}
      setEntryRootNode={setEntryRootNode}
      fireForwardStepClick={fireForwardStepClick}
      entryProductClickSuppressedRef={entryProductClickSuppressedRef}
      productFields={productFields}
      customDimensionMappings={customDimensionMappings}
      configuredEventContext={configuredEventContext}
    >
      {children}
    </TrackingCoreProvider>
  );
};

interface TrackingCoreProviderProps {
  readonly children: ReactNode;
  readonly host: TrackingHostContext & { readonly policy: 'flow-defaults' };
  readonly eventContext: DynamicExperienceEventContext | undefined;
  readonly stepCorrelation: StepCorrelation;
  readonly triggers: TrackingTriggers;
  readonly entryPointCorrelation: EntryPointCorrelation;
  readonly flowSessionId: string | undefined;
  readonly setEntryRootNode: (node: HTMLElement | null) => void;
  readonly fireForwardStepClick: (ctx: ForwardStepClickContext) => void;
  readonly entryProductClickSuppressedRef: React.MutableRefObject<boolean>;
  readonly productFields?: Readonly<Record<string, ProductFieldMapping>>;
  readonly customDimensionMappings?: readonly CustomDimensionMapping[];
  readonly configuredEventContext?: ConfiguredEventContext;
}

const TrackingCoreProvider: React.FC<TrackingCoreProviderProps> = ({
  children,
  host,
  eventContext,
  stepCorrelation,
  triggers,
  entryPointCorrelation,
  flowSessionId,
  setEntryRootNode,
  fireForwardStepClick,
  entryProductClickSuppressedRef,
  productFields,
  customDimensionMappings,
  configuredEventContext,
}) => {
  const composeDedupKey = useMemo(
    () =>
      (nodeId: string, binding?: BindingDedupIdentity): string => {
        const base = composeFlowElementKey({
          sessionId: flowSessionId,
          configId: stepCorrelation.dslConfigId,
          stepIndex: stepCorrelation.stepIndex,
          nodeId,
        });
        return binding ? appendBindingDedupKey(base, binding) : base;
      },
    [flowSessionId, stepCorrelation.dslConfigId, stepCorrelation.stepIndex],
  );
  const value = useMemo<TrackingContextValue>(
    () => ({
      host,
      eventContext,
      stepCorrelation,
      triggers,
      entryPointCorrelation,
      flowSessionId,
      composeDedupKey,
      setEntryRootNode,
      fireForwardStepClick,
      entryProductClickSuppressedRef,
      productFields,
      customDimensionMappings,
      configuredEventContext,
      sendConfiguredEvent,
    }),
    [
      host,
      eventContext,
      stepCorrelation,
      triggers,
      entryPointCorrelation,
      flowSessionId,
      composeDedupKey,
      setEntryRootNode,
      fireForwardStepClick,
      entryProductClickSuppressedRef,
      productFields,
      customDimensionMappings,
      configuredEventContext,
      sendConfiguredEvent,
    ],
  );
  return (
    <TrackingContext.Provider value={value}>
      {children}
    </TrackingContext.Provider>
  );
};

export interface DefaultOnTrackingProviderProps {
  readonly children: ReactNode;
  readonly page: Readonly<Record<string, unknown>>;
  readonly dedupScope: {
    readonly sessionId: string;
    readonly configId: string;
  };
  /**
   * Published config version. Included on `configuredEventContext`
   * when it is an integer of at least 1. A host that has no version
   * omits the key.
   */
  readonly dslConfigVersion?: number;
  readonly formContext: HostFormContext;
  /** Wins over the generic default of 1. */
  readonly viewThreshold?: number;
  /**
   * Per-component tracking defaults. An authored
   * `_meta.tracking.customDimensions` map replaces the default map
   * entirely. Key-by-key merge is not the default.
   */
  readonly componentTrackingDefaults?: Readonly<Record<string, TrackingMeta>>;
}

function hostPageString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

const isPlainMetadataObject = (
  value: unknown,
): value is Readonly<Record<string, unknown>> => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }

  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};

/**
 * Inert callable handed out for every flow-only surface a
 * step-free host publishes. Those hosts emit Element viewed/clicked
 * and field events only: the step-shaped Product / Field / Form
 * lifecycle, the entry-root observer, and the forward-click firer have
 * no meaning without a flow. Keeping the surface present but inert is
 * what lets consumers read `useTracking()` without branching on the
 * host — a node that reaches one of these paths fails closed
 * instead of beaming a step-shaped payload.
 */
const inertFlowSurface = (): 'rejected' => 'rejected';

function sendConfiguredEvent(
  eventName: string,
  payload: Record<string, unknown>,
): HostEventTransportResult {
  return tagularEvent(eventName, payload);
}

/**
 * Step-free correlation for `explicit-node-enable` hosts. Every field
 * is empty so `buildStepCorrelationOverride` contributes no step
 * dimensions, and no `dexp-step` page category is implied.
 */
const STEP_FREE_CORRELATION: StepCorrelation = {
  stepId: '',
  stepIndex: 0,
  stepType: '',
};

const DefaultOnHostProvider: React.FC<DefaultOnTrackingProviderProps> = ({
  children,
  page,
  dedupScope,
  dslConfigVersion,
  formContext,
  viewThreshold,
  componentTrackingDefaults,
}) => {
  const policy = 'registered-defaults' as const;
  const hasHost =
    isPlainMetadataObject(page) &&
    typeof dedupScope?.sessionId === 'string' &&
    dedupScope.sessionId.length > 0 &&
    typeof dedupScope?.configId === 'string' &&
    dedupScope.configId.length > 0;
  const host = useMemo<TrackingHostContext>(
    () => ({
      page: isPlainMetadataObject(page) ? createHostTrackingPage(page) : {},
      dedupScope: {
        sessionId: dedupScope?.sessionId,
        configId: dedupScope?.configId,
      },
      policy,
      ...(viewThreshold !== undefined ? { viewThreshold } : {}),
      formContext,
      ...(componentTrackingDefaults ? { componentTrackingDefaults } : {}),
    }),
    [
      page,
      dedupScope?.sessionId,
      dedupScope?.configId,
      formContext,
      viewThreshold,
      componentTrackingDefaults,
      policy,
    ],
  );
  const eventContext = useMemo(
    () =>
      hasHost
        ? {
            ...createHostElementEventContext(page),
            ...(formContext.formId ? { formId: formContext.formId } : {}),
            formName: formContext.formName,
            formType: formContext.formType,
          }
        : undefined,
    [hasHost, page, formContext],
  );
  const configuredEventContext = useMemo(
    () =>
      hasHost
        ? buildConfiguredEventContext({
            dslConfigId: dedupScope.configId,
            dslConfigVersion,
            pageCategory: hostPageString(page.pageCategory),
            contentGroup: hostPageString(page.contentGroup),
          })
        : undefined,
    [hasHost, dedupScope?.configId, dslConfigVersion, page],
  );
  const allTriggers = useTrackingEventTriggers(eventContext, policy);
  const triggers = useMemo<TrackingTriggers>(
    () => ({
      triggerElementLoadedEvent: allTriggers.triggerElementLoadedEvent,
      triggerProductLoadedEvent: inertFlowSurface,
      triggerStepElementLoadedEvent: inertFlowSurface,
      triggerStepElementViewedEvent: inertFlowSurface,
      triggerProductClickedEvent: inertFlowSurface,
      triggerProductViewedEvent: inertFlowSurface,
      triggerFieldEvents: allTriggers.triggerFieldEvents,
      triggerFormStartedEvent: inertFlowSurface,
      triggerFormContinuedEvent: inertFlowSurface,
      triggerFormViewedEvent: inertFlowSurface,
      triggerFormEvents: inertFlowSurface,
      triggerElementViewedEvent: allTriggers.triggerElementViewedEvent,
      triggerElementClickedEvent: allTriggers.triggerElementClickedEvent,
    }),
    [
      allTriggers.triggerElementLoadedEvent,
      allTriggers.triggerElementViewedEvent,
      allTriggers.triggerElementClickedEvent,
      allTriggers.triggerFieldEvents,
    ],
  );
  const composeDedupKey = useMemo(
    () =>
      (nodeId: string, binding?: BindingDedupIdentity): string => {
        const base = composeHostElementKey({
          sessionId: host.dedupScope.sessionId,
          configId: host.dedupScope.configId,
          nodeId,
        });
        return binding ? appendBindingDedupKey(base, binding) : base;
      },
    [host.dedupScope.sessionId, host.dedupScope.configId],
  );
  const value = useMemo<TrackingContextValue>(
    () => ({
      host,
      eventContext,
      stepCorrelation: STEP_FREE_CORRELATION,
      triggers,
      entryPointCorrelation: {
        elementLoadedCorrelationId: undefined,
        viewCorrelationId: undefined,
        formViewedCorrelationId: undefined,
      },
      flowSessionId: undefined,
      composeDedupKey,
      setEntryRootNode: inertFlowSurface,
      fireForwardStepClick: inertFlowSurface,
      configuredEventContext,
      sendConfiguredEvent,
    }),
    [
      host,
      eventContext,
      triggers,
      composeDedupKey,
      configuredEventContext,
      sendConfiguredEvent,
    ],
  );
  useEffect(() => {
    if (!hasHost) {
      console.warn('[tracking] missing-host-context: dispatch suppressed');
    }
  }, [hasHost]);

  if (!hasHost) {
    return (
      <TrackingContext.Provider value={undefined}>
        {children}
      </TrackingContext.Provider>
    );
  }

  return (
    <TrackingContext.Provider value={value}>
      {children}
    </TrackingContext.Provider>
  );
};

export const DefaultOnTrackingProvider: React.FC<
  DefaultOnTrackingProviderProps
> = (props) => <DefaultOnHostProvider {...props} />;

/**
 * Read the current `TrackingContextValue`. Returns `undefined` when
 * called outside a `<TrackingProvider>` rather than throwing — the
 * binding-enhancer-bound `Trackable` may render in unit tests or
 * legacy contexts that have no tracking surface, and silently
 * no-oping is preferable to crashing the render.
 */
export const useTracking = (): TrackingContextValue | undefined =>
  useContext(TrackingContext);

/** @internal — exported for tests that need to assert provider absence. */
export const __TrackingContext = TrackingContext;
